'use client';

import React, { useEffect, useRef } from 'react';
import { addToCartAtom, memberRedirectAtom } from '@/state';
import { integerFormatter } from '@/utils/products';
import { Box, Button, Card, CardActions, CardContent } from '@mui/material';
import { useSetAtom } from 'jotai';
import { usePathname, useRouter } from 'next/navigation';
import { sendGTMEvent } from '@next/third-parties/google';

interface ProfessionalCTAProps {
  isLoggedIn: boolean;
  product: CartProduct | null;
}

export const ProfessionalCTA = ({
  isLoggedIn,
  product
}: ProfessionalCTAProps) => {
  const addToCart = useSetAtom(addToCartAtom);
  const effectRan = useRef<boolean>(false);

  //#region Membership redirect
  // If we're displaying a Professional membership CTA, then cache the page pathname for redirection; if the
  // user signs up via this CTA, then we want to return them to this page following a successful signup
  const pathname = usePathname();
  const setMemberRedirect = useSetAtom(memberRedirectAtom);
  useEffect(() => {
    setMemberRedirect(pathname);
  }, []);
  //#region Membership redirect

  useEffect(() => {
    if (!effectRan.current) {
      sendGTMEvent({
        event: 'cta_shown',
        cta_type: 'professional'
      });
    }
    effectRan.current = true;
  }, []);

  const router = useRouter();
  return (
    <Card
      variant='outlined'
      color='secondary'
      sx={{ padding: { xs: '20px', sm: '30px', md: '40px' } }}
      className='paywall-container'
    >
      <CardContent>
        <Box>
          <p className='tagline'>TUD+ Professional Member Content</p>
          <h1 className='article__name'>Want to watch the recording?</h1>
        </Box>
        <Box>
          <p>
            TUD+ Professional Membership includes unlimited access to our entire
            catalogue of on-demand webinars. Log in or subscribe now to watch
            the full recording of this webinar.
          </p>
          <p>
            You can also access this webinar with an one-off payment.
            You&apos;ll need to create a free account to make a purchase.
          </p>
        </Box>
      </CardContent>
      <CardActions className='button-container'>
        {product ? (
          <Button
            href={
              isLoggedIn
                ? `/signup?ondemand=${encodeURIComponent(product.id)}&next=${encodeURIComponent(pathname)}`
                : `/signup?ondemand=${encodeURIComponent(product.id)}&next=${encodeURIComponent(pathname)}`
            }
            style={{
              background: 'none',
              border: '1px solid var(--Secondary-Grey-60, #959595)'
            }}
            variant='contained'
            disableElevation
          >
            Buy now for {integerFormatter.format(product.price)}
          </Button>
        ) : null}
        <Button
          href={
            isLoggedIn
              ? `/signup?product=Professional&next=${encodeURIComponent(pathname)}`
              : `/signup?product=Professional&next=${encodeURIComponent(pathname)}`
          }
          style={{ marginLeft: '0', textAlign: 'center' }}
          variant='contained'
          disableElevation
        >
          {'Become a Professional Member'}
        </Button>
      </CardActions>
    </Card>
  );
};
